import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import ConsumptionDetailModal from './ConsumptionDetailModal';

const ConsumptionList = ({ tasks }) => {
    const [selectedTask, setSelectedTask] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleViewMore = (task) => {
        setSelectedTask(task);
        setIsModalOpen(true);
    };

    return (
        <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 p-5 bg-[#F8FAFC] border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-widest">
                    <div className="col-span-4">TASK & STAGE</div>
                    <div className="col-span-6 text-center">MATERIAL UTILIZATION</div>
                    <div className="col-span-2 text-right">USAGE</div>
                </div>

                {/* Task Rows */}
                <div className="">
                    {(!tasks || tasks.length === 0) ? (
                        <p className="p-8 text-sm text-gray-400 text-center">No planned or released materials for this project on the selected date.</p>
                    ) : tasks.map((task, index) => (
                        <div key={task.id} className={`${index !== tasks.length - 1 ? 'border-b-[12px] border-[#F1F5F9]' : ''} relative`}>
                            <div className="grid grid-cols-12 gap-4 p-8 items-start hover:bg-gray-50 transition-colors">
                                {/* Task & Stage Column */}
                                <div className="col-span-4">
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">{task.name}</h3>
                                    <p className="text-sm text-gray-400 font-medium">{task.tower}</p>
                                    <p className="text-sm text-gray-400 font-medium">{task.stage}</p>
                                </div>

                                {/* Material Utilization Column */}
                                <div className="col-span-6 space-y-6">
                                    {task.materials.map((material, idx) => (
                                        <div key={idx} className="grid grid-cols-12 items-center gap-2">
                                            <div className="col-span-7 flex items-center gap-3">
                                                {material.status === 'danger' && <AlertCircle size={18} className="text-red-500 fill-red-50" />}
                                                <div className={`w-2 h-2 rounded-full ${material.status === 'danger' ? 'bg-red-500' : material.status === 'warning' ? 'bg-orange-500' : 'bg-gray-300'}`}></div>
                                                <span className="text-base font-semibold text-gray-700">{material.name}</span>
                                            </div>
                                            <div className="col-span-5 text-right font-medium text-lg">
                                                <span className="text-gray-900">{material.planned}</span>
                                                <span className="text-gray-400 mx-2">/</span>
                                                <span className={`${material.status === 'danger' ? 'text-red-500' : material.status === 'warning' ? 'text-orange-500' : 'text-gray-400'} font-bold`}>
                                                    {material.consumed}
                                                </span>
                                                <span className="text-gray-400 ml-2 text-sm font-normal">{material.unit}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Usage Column */}
                                <div className="col-span-2 space-y-6">
                                    {task.materials.map((material, idx) => (
                                        <div key={idx} className="text-right h-[27px] flex items-center justify-end">
                                            <span className={`text-lg font-bold ${material.status === 'danger' ? 'text-red-500' : material.status === 'warning' ? 'text-orange-500' : 'text-gray-400'}`}>
                                                {material.usage}%
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* View More Materials Button (Conditional: only if 3+ total materials) */}
                            {task.allMaterials && task.allMaterials.length > 3 && (
                                <button
                                    onClick={() => handleViewMore(task)}
                                    className="w-full py-3 bg-[#F8FAFC] text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] flex items-center justify-center gap-2 hover:bg-gray-100 transition-colors border-t border-gray-100 mb-4"
                                >
                                    <span className="text-lg leading-none opacity-40">...</span> VIEW {task.allMaterials.length - 3} MORE MATERIALS
                                </button>
                            )}

                            {/* Shadow Effect for the separator */}
                            {index !== tasks.length - 1 && (
                                <div className="absolute bottom-[-12px] left-0 right-0 h-[1px] bg-gray-200/50 shadow-[0_1px_2px_rgba(0,0,0,0.05)]"></div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <ConsumptionDetailModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                task={selectedTask}
            />
        </>
    );
};

export default ConsumptionList;
